import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import {
  isLegacySmsPopupSelectionMessage,
  type LegacySmsContact,
  type LegacySmsRecipientMode,
  type LegacySmsRecipientRow,
} from '../../mock/legacySmsRecipients'
import { LEGACY_SMS_POPUP_CHANNEL } from './LegacySmsPopupBridge'
import './LegacySmsRecipients.css'

const modeNames: Record<LegacySmsRecipientMode, string> = {
  customer: '고객선택 발송',
  shoot: '촬영선택 발송',
  phone: '전화번호로 발송',
}

export type LegacySmsRecipientSelection = {
  rows: LegacySmsRecipientRow[]
  selectedContacts: Array<{ row: LegacySmsRecipientRow; contact: LegacySmsContact; content: string }>
  rowCount: number
  sendCount: number
}

export type LegacySmsRecipientsProps = {
  message: string
  officeId?: string
  officeName?: string
  initialMode?: LegacySmsRecipientMode
  initialRows?: LegacySmsRecipientRow[]
  allowPopupAdd?: boolean
  onSelectionChange?: (selection: LegacySmsRecipientSelection) => void
  onNotice?: (message: string) => void
}

function todayText() {
  const now = new Date()
  return `${now.getFullYear()}년 ${now.getMonth() + 1}월 ${now.getDate()}일`
}

function todayIsoText() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

function phoneWithHyphen(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
  if (digits.length === 10) return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
  return digits
}

export function replaceLegacySmsRecipientCodes(message: string, row: LegacySmsRecipientRow, officeName = 'Mock 지점 A') {
  const mother = row.contacts.find((contact) => contact.kind === 'mother')
  const father = row.contacts.find((contact) => contact.kind === 'father')
  const replacements: Record<string, string> = {
    '#{고번}': row.customerNo,
    '#{엄마}': mother?.name ?? '',
    '#{엄마폰}': mother?.phone ?? '',
    '#{아빠}': father?.name ?? '',
    '#{아빠폰}': father?.phone ?? '',
    '#{아기}': row.babyName,
    '#{OOPSID}': row.customerNo,
    '#{오늘}': todayText(),
    '#{스튜명칭}': officeName,
    '#{촬일}': row.scheduleDate ?? '',
    '#{촬시}': row.photoStart ?? '',
    '#{촬시2}': row.photoEnd ?? '',
    '#{유입경로}': row.source,
    '#{산부인과}': row.hospital,
    '#{조리원}': row.postnatalCare,
    '#{추가경로1}': row.additionalSource1,
    '#{추가경로2}': row.additionalSource2,
  }
  return Object.entries(replacements).reduce((content, [code, value]) => content.replaceAll(code, value), message)
}

export function LegacySmsRecipients({ message, officeId = 'mock-office', officeName = 'Mock 지점 A', initialMode = 'customer', initialRows = [], allowPopupAdd = true, onSelectionChange, onNotice }: LegacySmsRecipientsProps) {
  const [mode, setMode] = useState<LegacySmsRecipientMode>(initialMode)
  const [rows, setRows] = useState<LegacySmsRecipientRow[]>(initialRows)
  const [checkedContactIds, setCheckedContactIds] = useState<string[]>(() => initialRows.flatMap((row) => row.contacts.filter((contact) => contact.defaultChecked).map((contact) => contact.id)))
  const [phone, setPhone] = useState('')
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)
  const [localNotice, setLocalNotice] = useState<string | null>(null)
  const [pendingMode, setPendingMode] = useState<LegacySmsRecipientMode | null>(null)
  const [pendingPhone, setPendingPhone] = useState<string | null>(null)
  const previousOfficeId = useRef(officeId)

  const notify = (text: string) => onNotice ? onNotice(text) : setLocalNotice(text)
  const allContactIds = useMemo(() => rows.flatMap((row) => row.contacts.map((contact) => contact.id)), [rows])
  const selectedContacts = useMemo(() => rows.flatMap((row) => row.contacts
    .filter((contact) => checkedContactIds.includes(contact.id))
    .map((contact) => ({ row, contact, content: replaceLegacySmsRecipientCodes(message, row, officeName) }))), [checkedContactIds, message, officeName, rows])

  useEffect(() => {
    onSelectionChange?.({ rows, selectedContacts, rowCount: rows.length, sendCount: selectedContacts.length })
  }, [onSelectionChange, rows, selectedContacts])

  useEffect(() => {
    if (previousOfficeId.current === officeId) return
    previousOfficeId.current = officeId
    setMode('customer')
    setRows([])
    setCheckedContactIds([])
    setPhone('')
  }, [officeId])

  useEffect(() => {
    const applyPopupSelection = (data: unknown) => {
      if (!isLegacySmsPopupSelectionMessage(data)) return
      const nextMode: LegacySmsRecipientMode = data.type === 'legacy-sms-shoot-selection' ? 'shoot' : 'customer'
      const incoming = data.recipients.filter((row) => row.mode === nextMode)
      setMode(nextMode)
      setRows((current) => {
        const kept = current.filter((row) => row.mode === nextMode)
        return [...kept, ...incoming.filter((row) => !kept.some((item) => item.id === row.id))].slice(0, 1000)
      })
      setCheckedContactIds((current) => Array.from(new Set([
        ...current,
        ...incoming.flatMap((row) => row.contacts.filter((contact) => contact.defaultChecked).map((contact) => contact.id)),
      ])))
    }
    const receivePopupSelection = (event: MessageEvent<unknown>) => {
      if (event.origin !== window.location.origin) return
      applyPopupSelection(event.data)
    }
    window.addEventListener('message', receivePopupSelection)
    const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(LEGACY_SMS_POPUP_CHANNEL)
    if (channel) channel.onmessage = (event) => applyPopupSelection(event.data)
    return () => {
      window.removeEventListener('message', receivePopupSelection)
      channel?.close()
    }
  }, [])

  const clearAndChangeMode = (nextMode: LegacySmsRecipientMode) => {
    setMode(nextMode)
    setRows([])
    setCheckedContactIds([])
    setPhone('')
    setPendingMode(null)
  }

  const requestModeChange = (nextMode: LegacySmsRecipientMode) => {
    if (nextMode === mode) return
    if (rows.length) setPendingMode(nextMode)
    else clearAndChangeMode(nextMode)
  }

  const appendRows = (incoming: LegacySmsRecipientRow[]) => {
    setRows((current) => {
      const additions = incoming.filter((row) => !current.some((item) => item.id === row.id))
      if (current.length + additions.length > 1000) notify('수신 고객 목록은 최대 1,000개까지 추가 가능합니다.')
      return [...current, ...additions].slice(0, 1000)
    })
    setCheckedContactIds((current) => Array.from(new Set([
      ...current,
      ...incoming.flatMap((row) => row.contacts.filter((contact) => contact.defaultChecked).map((contact) => contact.id)),
    ])))
  }

  const openPopup = (url: string) => {
    const popup = window.open(url, '', 'popup=yes,width=1080,height=800,top=50,left=50,resizable=yes,scrollbars=yes')
    if (!popup) notify('팝업이 차단되었습니다. 브라우저에서 팝업을 허용해 주세요.')
  }

  const openCustomerPopup = () => {
    const params = new URLSearchParams({ page_no: '1', page_start: '1', page_count: '30', caller: 'sms', office_id: officeId })
    openPopup(`/v1/customer_search/?${params}`)
  }

  const openShootPopup = () => {
    const day = todayIsoText()
    const params = new URLSearchParams({ office_id: officeId, status_contents: '0', val: '', start_day: day, end_day: day, page_no: '1', page_start: '1', page_count: '30', caller: 'sms' })
    openPopup(`/v1/filmingsearch/?${params}`)
  }

  const requestPhoneAdd = () => {
    const input = phone.trim().slice(0, 11)
    if (!input) { notify('전화번호를 입력해 주세요.'); return }
    const formatted = phoneWithHyphen(input)
    setPendingPhone(formatted)
  }

  const confirmPhoneAdd = () => {
    if (!pendingPhone) return
    const suffix = pendingPhone.replace(/\D/g, '').slice(-4)
    const row: LegacySmsRecipientRow = {
      id: `mock-direct-${Date.now()}`, mode: 'phone', customerNo: '', babyName: '', oopsId: '', source: '', hospital: '', postnatalCare: '', additionalSource1: '', additionalSource2: '',
      contacts: [{ id: `mock-direct-${Date.now()}-${suffix}`, kind: 'direct', relation: '', name: '', phone: pendingPhone, defaultChecked: true }],
    }
    appendRows([row])
    setPhone('')
    setPendingPhone(null)
  }

  const toggleAll = () => setCheckedContactIds((current) => allContactIds.length > 0 && allContactIds.every((id) => current.includes(id)) ? [] : allContactIds)
  const toggleContact = (id: string) => setCheckedContactIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id])
  const hasShootRows = mode === 'shoot' && rows.some((row) => row.mode === 'shoot')

  return <section className="g_card l_section_recv l_calling_list legacy-m01-recipients">
    <aside className="l_sms_func">{(Object.keys(modeNames) as LegacySmsRecipientMode[]).map((item, index) => <Fragment key={item}>{index > 0 ? ' ' : null}<button className="g_btn category tab" data-selected={mode === item ? 'true' : undefined} type="button" onClick={() => requestModeChange(item)}>{modeNames[item]}</button></Fragment>)}</aside>

    <div className={`l_add_list_func ${mode === 'shoot' ? 'l_init_style' : ''}`}>
      <div className={`g_input_wrap l_not_popup ${mode === 'customer' && allowPopupAdd ? '' : 'l_non_display'}`}><span className="g_label title l_non_display">수신목록</span><button id="l_add_customer" className="g_btn add size_1" type="button" onClick={openCustomerPopup}>수신 고객 추가</button></div>
      <form id="l_call_form" onSubmit={(event) => { event.preventDefault(); requestPhoneAdd() }}><div className={`g_input_wrap l_add_call_list ${mode === 'phone' ? '' : 'l_non_display'}`}><span className="g_label title">전화번호</span><input id="phone-add" className="g_input" inputMode="tel" value={phone} maxLength={11} onChange={(event) => setPhone(event.target.value.replace(/[^0-9-]/g, '').slice(0, 11))} required /><button id="l_add_phone" className="g_btn add size_1 g_btn_submit" type="submit">추가</button></div></form>
      <div className={`g_input_wrap l_shoot_append_fun_wrap ${mode === 'shoot' ? '' : 'l_non_display'}`}><span className="g_label title">수신목록</span><button id="l_add_shoot" className="g_btn add size_1" type="button" onClick={openShootPopup}>촬영 고객 추가</button></div>
      <div className="g_input_wrap l_row_info"><span className="g_label title">추가된 고객 수</span><span id="l_row_count">{rows.length.toLocaleString()}</span><span id="l_max_row">/1,000</span></div>
    </div>

    <article className="g_list_wrap g_scroll_bar">
      <table className="g_list l_recv_table" id="sms-table">
        <thead><tr><th className="g_list_index">NO</th><th>고객번호</th><th><div className="l_customer_info_header"><input id="l_checkbox_all" className="g_input checkbox g_checked_toggle_all" type="checkbox" checked={allContactIds.length > 0 && allContactIds.every((id) => checkedContactIds.includes(id))} onChange={toggleAll} />성함, 연락처</div></th><th>아기</th><th id="l_schedule_date_head" className={hasShootRows ? '' : 'l_non_display'}>촬영일</th><th id="l_photo_time_head" className={hasShootRows ? '' : 'l_non_display'} colSpan={2}>시각</th><th>발송내용</th></tr></thead>
        <tbody>{rows.map((row, index) => <tr className="g_click_item l_data_row" key={row.id}>
          <td className="l_row_no">{index + 1}</td>
          <td className="l_customer_no">{row.customerNo}</td>
          <td className="l_customer_info_col">{row.contacts.map((contact) => <div className="l_customer_box" key={contact.id}>
            <span className="l_box_item"><input className="g_input checkbox g_checked_toggle" type="checkbox" aria-label={`${contact.name || contact.relation} ${contact.phone} 수신 선택`} checked={checkedContactIds.includes(contact.id)} onChange={() => toggleContact(contact.id)} /></span>
            <div className="l_box_item"><span>{contact.relation}</span></div><div className="l_box_item"><span>{contact.name}</span></div><div className="l_box_item type_2x"><span>{contact.phone}</span></div>
          </div>)}</td>
          <td>{row.babyName}</td>
          <td className={row.mode === 'shoot' ? '' : 'l_non_display'}>{row.scheduleDate || '-'}</td><td className={row.mode === 'shoot' ? '' : 'l_non_display'}>{row.photoStart || '-'}</td><td className={row.mode === 'shoot' ? '' : 'l_non_display'}>{row.photoEnd || '-'}</td>
          <td className="l_hover_container"><button className="l_hover" type="button" onMouseEnter={() => setHoveredRowId(row.id)} onMouseLeave={() => setHoveredRowId(null)} onFocus={() => setHoveredRowId(row.id)} onBlur={() => setHoveredRowId(null)}>내용보기</button>{hoveredRowId === row.id && <span className="l_hover_layer show" role="tooltip">{message ? replaceLegacySmsRecipientCodes(message, row, officeName) : '내용 없음'}</span>}</td>
        </tr>)}</tbody>
      </table>
      {!rows.length && <div className="l_non_array">수신 목록이 비어있습니다.</div>}
    </article>

    {pendingMode && <div className="m01-modal legacy-m01-recipient-modal"><div className="mini_modal"><p>수신자 목록이 초기화됩니다. 변경하시겠습니까?</p><div className="flex_wrap g_modal_button_wrap"><button className="g_btn save" type="button" onClick={() => clearAndChangeMode(pendingMode)}>예(Yes)</button><button className="g_btn close" type="button" onClick={() => setPendingMode(null)}>아니오(No)</button></div></div></div>}
    {pendingPhone && <div className="m01-modal legacy-m01-recipient-modal"><div className="mini_modal"><p><strong>{pendingPhone}</strong><br />입력하신 번호를 수신자 목록에 추가하시겠습니까?</p><div className="flex_wrap g_modal_button_wrap"><button className="g_btn save" type="button" onClick={confirmPhoneAdd}>예(Yes)</button><button className="g_btn close" type="button" onClick={() => setPendingPhone(null)}>아니오(No)</button></div></div></div>}
    {localNotice && <div className="m01-notice">{localNotice}<button type="button" onClick={() => setLocalNotice(null)}>×</button></div>}
  </section>
}
